import client from './client';

/**
 * Generate and download receipt PDF
 * @param {number} saleId - Sale ID
 * @param {boolean} download - Whether to download or preview
 */
export const generateReceipt = async (saleId, download = true) => {
  try {
    const response = await client.get(`/receipts/${saleId}/generate`, {
      params: { download: download.toString() },
      responseType: 'blob',
      timeout: 60000, // 60 seconds for receipt generation (handles cold starts)
    });
    return response.data;
  } catch (error) {
    // Add more context to timeout errors
    if (error.code === 'ECONNABORTED') {
      const timeoutError = new Error(
        'Receipt generation timed out. This may be due to server cold start. Please try again.'
      );
      timeoutError.code = 'TIMEOUT';
      throw timeoutError;
    }
    throw error;
  }
};

/**
 * Generate and download receipt as image (PNG)
 * @param {number} saleId - Sale ID
 * @param {boolean} download - Whether to download or preview
 */
export const generateReceiptImage = async (saleId, download = true) => {
  try {
    const response = await client.get(`/receipts/${saleId}/image`, {
      params: { download: download.toString() },
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Email receipt to customer
 * @param {number} saleId - Sale ID
 * @param {string} recipientEmail - Email address
 * @param {string} additionalMessage - Optional additional message
 */
export const emailReceipt = async (saleId, recipientEmail, additionalMessage = '') => {
  try {
    const response = await client.post(`/receipts/${saleId}/email`, {
      recipientEmail,
      additionalMessage,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get receipt configuration
 */
export const getReceiptConfig = async () => {
  try {
    const response = await client.get('/receipts/config');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update receipt configuration
 * @param {object} configData - Configuration data
 */
export const updateReceiptConfig = async (configData) => {
  try {
    const response = await client.put('/receipts/config', configData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Generate receipt preview
 * @param {Object} config - Receipt configuration to preview
 * @returns {Promise<Blob>} - Receipt preview image blob
 */
export const getReceiptPreview = async (config) => {
  try {
    const response = await client.post('/receipts/preview-render', config, {
      responseType: 'blob',
      timeout: 60000, // 60 seconds for preview generation (handles cold starts)
    });
    return response.data;
  } catch (error) {
    // Add more context to timeout errors
    if (error.code === 'ECONNABORTED') {
      const timeoutError = new Error(
        'Preview generation timed out. This may be due to server cold start. Please try again.'
      );
      timeoutError.code = 'TIMEOUT';
      throw timeoutError;
    }
    throw error;
  }
};

/**
 * Get receipt history for a customer
 * @param {number} customerId - Customer ID
 * @param {number} page - Page number
 * @param {number} limit - Records per page
 */
export const getReceiptHistory = async (customerId, page = 1, limit = 20) => {
  try {
    const response = await client.get(`/receipts/history/${customerId}`, {
      params: { page, limit },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all receipts with filters
 * @param {object} filters - Filter parameters
 */
export const getAllReceipts = async (filters = {}) => {
  try {
    const response = await client.get('/receipts', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get receipt data for a sale (includes combined total for joint sales)
 * @param {number} saleId - Sale ID
 */
export const getReceiptData = async (saleId) => {
  try {
    const response = await client.get(`/receipts/${saleId}/data`);
    return response.data;
  } catch (error) {
    throw error;
  }
};
